/**
 * ShopScene - Buy upgrades and pieces with gold.
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { getUpgradeOffers, Upgrade } from '../game/upgrades/UpgradeLibrary';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { PieceType } from '../types';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, RARITY, RARITY_HEX, makeButton, makePanel, paintBackground, fadeInScene } from '../constants/Theme';

interface PieceItem { type: PieceType; name: string; price: number; }

export class ShopScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private shopItems: (Upgrade | PieceItem)[] = [];
  private goldText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: SCENES.SHOP });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);
    AudioManager.getInstance().init(this);

    const title = this.add.text(cx, 70, 'SHOP', T.title({ color: INK.gold }));
    title.setOrigin(0.5);
    title.setLetterSpacing(4);

    // Gold pill
    makePanel(this, cx, 128, 240, 50, { fill: PALETTE.panel, radius: 25 });
    this.goldText = this.add.text(cx, 128, '', T.label({ color: INK.gold }));
    this.goldText.setOrigin(0.5);
    this.updateGoldDisplay();

    this.generateShopItems();

    let y = 200;
    const spacing = 142;
    for (const item of this.shopItems) {
      if ('effect' in item) this.createUpgradeCard(cx, y, item as Upgrade);
      else this.createPieceCard(cx, y, item as PieceItem);
      y += spacing;
    }

    makeButton(this, cx, HEIGHT - 80, 'LEAVE SHOP', {
      fill: PALETTE.blue, onClick: () => { this.gameStateManager.saveGame(); this.scene.start(SCENES.MAP); },
    });
  }

  private generateShopItems(): void {
    const upgrades = getUpgradeOffers(6, this.gameStateManager.getOfferContext('normal'))
      .filter(u => !(u.effect.type === 'gold' && u.effect.target === 'immediate'))
      .slice(0, 3);
    const floor = this.gameStateManager.getCurrentFloor();
    const pool: PieceType[] = floor >= 8 ? ['p', 'n', 'b', 'r', 'q'] : ['p', 'n', 'b', 'r'];
    const pieces: PieceItem[] = pool.sort(() => Math.random() - 0.5).slice(0, 2)
      .map(type => ({ type, name: this.getPieceName(type), price: this.getPiecePrice(type) }));
    this.shopItems = [...upgrades, ...pieces];
  }

  private createUpgradeCard(x: number, y: number, upgrade: Upgrade): void {
    const price = this.getUpgradePrice(upgrade.rarity);
    this.buildCard(x, y, {
      accent: RARITY_HEX[upgrade.rarity], accentInk: RARITY[upgrade.rarity],
      name: upgrade.name, desc: upgrade.description, price, badge: upgrade.rarity.toUpperCase(),
      onBuy: () => { this.gameStateManager.applyUpgrade(upgrade); AudioManager.getInstance().play('coin'); },
    });
  }

  private createPieceCard(x: number, y: number, piece: PieceItem): void {
    this.buildCard(x, y, {
      accent: PALETTE.green, accentInk: INK.green, name: piece.name, desc: 'Add to your army', price: piece.price,
      sprite: `w${piece.type}`,
      onBuy: () => { if (piece.type !== 'k') this.gameStateManager.addPiece(piece.type); this.gameStateManager.saveGame(); AudioManager.getInstance().play('coin'); },
    });
  }

  private buildCard(
    x: number, y: number,
    o: { accent: number; accentInk: string; name: string; desc: string; price: number; badge?: string; sprite?: string; onBuy: () => void }
  ): void {
    const w = 640, h = 122;
    let affordable = this.gameStateManager.getGameState().gold >= o.price;
    let sold = false;

    const panel = makePanel(this, x, y, w, h, { fill: PALETTE.panel, stroke: o.accent, strokeWidth: 3, radius: 16 });
    const setDim = () => panel.setAlpha(sold ? 0.4 : affordable ? 1 : 0.55);
    setDim();

    const textLeft = o.sprite ? x - w / 2 + 120 : x - w / 2 + 28;
    if (o.sprite) {
      const sp = this.add.sprite(x - w / 2 + 64, y, o.sprite);
      sp.setDisplaySize(78, 78);
    }
    if (o.badge) {
      const badge = this.add.text(textLeft, y - h / 2 + 14, o.badge, T.micro({ color: o.accentInk }));
      badge.setLetterSpacing(2);
    }
    const name = this.add.text(textLeft, y - 18, o.name, T.subheading({ color: o.accentInk, fontSize: '28px', fontStyle: '700' }));
    name.setOrigin(0, 0.5);
    const desc = this.add.text(textLeft, y + 22, o.desc, T.small({ color: INK.secondary, wordWrap: { width: w - (textLeft - (x - w / 2)) - 160 } }));
    desc.setOrigin(0, 0.5);

    const priceText = this.add.text(x + w / 2 - 26, y, `◆ ${o.price}`, T.label({ color: affordable ? INK.gold : INK.red }));
    priceText.setOrigin(1, 0.5);

    const hit = this.add.rectangle(x, y, w, h, 0x000000, 0.001).setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => { if (!sold && affordable) panel.setAlpha(0.82); });
    hit.on('pointerout', () => setDim());
    hit.on('pointerdown', () => {
      if (sold) return;
      affordable = this.gameStateManager.getGameState().gold >= o.price;
      if (!affordable) { AudioManager.getInstance().play('error'); return; }
      this.gameStateManager.addGold(-o.price);
      o.onBuy();
      sold = true;
      setDim();
      priceText.setText('SOLD');
      priceText.setColor(INK.faint);
      this.updateGoldDisplay();
      this.showFeedback(`Purchased: ${o.name}`);
    });
  }

  private updateGoldDisplay(): void {
    this.goldText.setText(`◆ ${this.gameStateManager.getGameState().gold} gold`);
  }

  private showFeedback(message: string): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const text = this.add.text(WIDTH / 2, HEIGHT / 2 - 240, message, T.subheading({
      color: INK.green, align: 'center', backgroundColor: '#0a0f1ccc', padding: { x: 18, y: 10 },
    }));
    text.setOrigin(0.5);
    text.setDepth(300);
    text.setAlpha(0);
    this.tweens.add({ targets: text, alpha: 1, duration: 250, yoyo: true, hold: 900, onComplete: () => text.destroy() });
  }

  private getUpgradePrice(rarity: string): number {
    return rarity === 'common' ? 50 : rarity === 'uncommon' ? 100 : rarity === 'rare' ? 150 : 250;
  }

  private getPiecePrice(type: PieceType): number {
    switch (type) {
      case 'p': return 30;
      case 'n': case 'b': return 75;
      case 'r': return 150;
      case 'q': return 300;
      default: return 100;
    }
  }

  private getPieceName(type: string): string {
    const names: Record<string, string> = { p: 'Pawn', n: 'Knight', b: 'Bishop', r: 'Rook', q: 'Queen', k: 'King' };
    return names[type] || 'Unknown';
  }
}
